<template>
  <SfModalPortal
    modal-id="report-account-details-modal"
    :size="modalSizes.fullscreen"
    :close-on-escape="false"
    content-class-name="tw-flex-1 tw-min-h-0 !tw-p-0 !tw-overflow-hidden"
    @opened="onOpened"
    @close="onClose"
    @close-attempt="onCloseAttempt"
  >
    <template #title>
      <div class="tw-flex tw-flex-row tw-gap-2 tw-items-center">
        <div class="tw-flex tw-flex-col tw-gap-0.5">
          <span
            v-if="accountSubtitle"
            class="tw-text-xs tw-font-normal tw-text-sf-neutral"
          >
            {{ accountSubtitle }}
          </span>
          <a
            v-if="accountLedgerUrl"
            :href="accountLedgerUrl"
            class="
              tw-text-2xl tw-font-medium tw-text-sf-primary-dark
              hover:tw-underline
            "
          >
            {{ accountTitle }}
          </a>
          <span
            v-else
            class="tw-text-2xl tw-font-medium"
            :class="
              presentationMode
                ? 'tw-text-neutral-900'
                : 'tw-text-sf-primary-dark'
            "
          >
            {{ accountTitle }}
          </span>
        </div>
        <span
          v-if="ledgerLabel"
          class="
            tw-rounded-full
            tw-bg-sf-snow-dark
            tw-text-sf-grey
            tw-text-xs
            tw-px-2
            tw-py-1.5
            tw-font-normal
          "
        >
          {{ ledgerLabel }}
        </span>
        <span v-if="reportValue" class="tw-text-xl tw-font-normal">
          {{ reportValue }}
        </span>
      </div>
    </template>

    <template #body>
      <div v-if="loading" class="tw-p-6">
        <RemarkSkeleton v-for="i in 4" :key="i" class="tw-my-1" />
      </div>
      <div
        v-else-if="errored"
        class="tw-flex tw-items-center tw-justify-center tw-p-6 tw-h-full"
      >
        <div class="tw-text-sm tw-text-sf-neutral-light-1 tw-text-center">
          {{ I18n.t('detailed_entry.load_error') }}
        </div>
      </div>
      <template v-else>
        <div
          class="
            tw-flex tw-flex-col
            lg:tw-flex-row
            tw-h-full tw-min-h-0 tw-overflow-y-auto
            lg:tw-overflow-hidden
          "
        >
          <!-- Left column: Adjustments & Transactions  -->
          <div
            class="
              tw-bg-slate-50
              tw-flex
              tw-flex-col
              tw-flex-1
              tw-min-w-0
              tw-min-h-0
              tw-overflow-hidden
              tw-rounded-md
            "
          >
            <div
              class="
                tw-flex
                tw-flex-col
                tw-flex-1
                tw-min-h-0
                tw-overflow-y-auto
                tw-gap-6
                tw-p-4
              "
            >
              <!-- Adjustments -->
              <div
                v-if="adjustments.entries.length"
                class="
                  tw-bg-white
                  tw-p-4
                  tw-rounded-md
                  tw-border
                  tw-border-solid
                  tw-border-sf-snow-dark
                  tw-flex-initial
                  tw-max-h-[calc(50%_-_12px)]
                  tw-flex tw-flex-col
                "
                data-testid="adjustments-table"
              >
                <div
                  class="
                    tw-text-sf-neutral-dark
                    tw-font-medium
                    tw-text-lg
                    tw-mb-3
                    tw-pb-2
                  "
                >
                  {{ I18n.t('adjustment.adjustments') }}
                </div>
                <div class="tw-flex-1 tw-min-h-0 tw-overflow-auto">
                  <SfTable
                    :headers="adjustments.headers"
                    :rows="adjustmentRows"
                    :handles-sorting="false"
                    :sticky-header="true"
                  >
                    <template #columns>
                      <tr>
                        <th
                          v-for="(
                            header, index
                          ) in adjustments.translatedHeaders"
                          :key="index"
                          :class="{
                            'sf-alignr': isValueHeader(
                              adjustments.headers[index]
                            ),
                          }"
                        >
                          <span>{{ header }}</span>
                        </th>
                      </tr>
                    </template>
                    <template #rows>
                      <tr
                        v-for="(entry, index) in adjustmentRows"
                        :key="index"
                        class="tw-group"
                        :class="{ 'tw-text-sf-neutral': entry.posted }"
                      >
                        <td
                          v-for="(
                            headerKey, headerIndex
                          ) in adjustments.headers"
                          :key="headerIndex"
                          :class="{ 'sf-alignr': isValueHeader(headerKey) }"
                        >
                          <template v-if="headerKey === 'description'">
                            <div class="tw-flex tw-items-center tw-gap-2">
                              <component
                                :is="
                                  isAdjustmentClickable(entry)
                                    ? 'button'
                                    : 'span'
                                "
                                :type="
                                  isAdjustmentClickable(entry)
                                    ? 'button'
                                    : undefined
                                "
                                :class="
                                  isAdjustmentClickable(entry)
                                    ? CLICKABLE_ADJUSTMENT_CLASS
                                    : ''
                                "
                                @click="
                                  isAdjustmentClickable(entry) &&
                                    openAdjustment(entry)
                                "
                              >
                                {{ entry.description }}
                              </component>
                              <span class="tw-text-xs tw-text-sf-neutral">
                                ({{ I18n.t('adjustment.adjustment') }} #{{
                                  entry.adjustmentUlid
                                }}
                                {{
                                  I18n.t(
                                    `adjustment.categories.${entry.category}`
                                  ).toLowerCase()
                                }})
                              </span>
                              <SfButtonDs
                                v-if="entry.canTogglePosted"
                                variant="secondary"
                                size="sm"
                                class="
                                  tw-ml-auto tw-opacity-0
                                  group-hover:tw-opacity-100
                                  focus:tw-opacity-100
                                "
                                :disabled="
                                  togglingUrls.has(entry.togglePostedUrl)
                                "
                                @click.stop="togglePosted(entry)"
                              >
                                {{
                                  entry.posted
                                    ? I18n.t(
                                        'adjustments.adjustment_block.unpost'
                                      )
                                    : I18n.t(
                                        'adjustments.adjustment_block.post'
                                      )
                                }}
                              </SfButtonDs>
                            </div>
                          </template>
                          <template v-else-if="headerKey === 'value'">
                            <component
                              :is="
                                isAdjustmentClickable(entry) ? 'button' : 'span'
                              "
                              :type="
                                isAdjustmentClickable(entry)
                                  ? 'button'
                                  : undefined
                              "
                              :class="
                                isAdjustmentClickable(entry)
                                  ? CLICKABLE_ADJUSTMENT_CLASS
                                  : ''
                              "
                              @click="
                                isAdjustmentClickable(entry) &&
                                  openAdjustment(entry)
                              "
                            >
                              {{ entry.value }}
                            </component>
                          </template>
                          <template v-else>
                            {{ entry[headerKey] ?? '' }}
                          </template>
                        </td>
                      </tr>
                    </template>
                  </SfTable>
                </div>
              </div>

              <!-- Transactions -->
              <div
                class="
                  tw-bg-white
                  tw-p-4
                  tw-rounded-md
                  tw-border
                  tw-border-solid
                  tw-border-sf-snow-dark
                  tw-flex-initial
                  tw-flex
                  tw-flex-col
                "
                :class="
                  adjustments.entries.length
                    ? 'tw-max-h-[calc(50%_-_12px)]'
                    : 'tw-max-h-full'
                "
                data-testid="entries-table"
              >
                <EntriesTable
                  :title="I18n.t('detailed_entry.ledger')"
                  :headers="detailedEntries.headers"
                  :list="detailedEntries.entries"
                  :translated-headers="detailedEntries.translatedHeaders"
                  :truncated="detailedEntries.truncated || ''"
                  :sticky-header="true"
                  :empty-banner-text="
                    I18n.t('detailed_entry.sidebar.empty_state.no_transactions')
                  "
                />
              </div>
            </div>
          </div>

          <!-- Right column: Top 5 & Messages / Document Preview -->
          <div
            class="tw-flex tw-flex-col tw-rounded-sm lg:tw-shrink-0"
            :class="
              documentPreview
                ? 'lg:tw-w-1/2 lg:tw-min-h-0 lg:tw-overflow-hidden'
                : 'lg:tw-w-[400px] lg:tw-overflow-y-auto'
            "
          >
            <!-- Document Preview -->
            <template v-if="documentPreview">
              <div
                class="
                  tw-flex
                  tw-flex-col
                  tw-flex-1
                  tw-min-h-0
                  tw-border-solid
                  tw-border-l
                  tw-border-r-0
                  tw-border-y-0
                  tw-border-sf-neutral-lighter-2
                "
              >
                <div
                  class="
                    tw-bg-white
                    tw-flex
                    tw-items-center
                    tw-justify-between
                    tw-p-4
                    tw-border-solid
                    tw-border-b
                    tw-border-x-0
                    tw-border-t-0
                    tw-border-sf-neutral-lighter-2
                  "
                >
                  <h3
                    class="
                      tw-m-0 tw-text-sf-neutral-dark tw-font-medium tw-text-lg
                    "
                  >
                    {{ I18n.t('file_preview_sidebar.title') }}
                  </h3>
                  <SfButtonDs
                    icon-only
                    icon-before="xmark"
                    variant="tertiary"
                    data-testid="close-document-preview"
                    @click="closeDocumentPreview"
                  />
                </div>
                <div
                  class="
                    document-preview-wrapper
                    tw-flex-1 tw-min-h-0 tw-px-4 tw-py-4
                  "
                >
                  <AttachmentListItem
                    :key="documentPreview.attachment.document_id"
                    :attachment="documentPreview.attachment"
                    :changes-are-disabled="true"
                    :single-attachment="true"
                    :document-preview="documentPreview.response"
                    preview-height="100%"
                  />
                </div>
              </div>
            </template>

            <!-- Default: Top 5 & Messages -->
            <template v-else>
              <!-- Top 5 -->
              <div v-if="top5.length" class="tw-p-4">
                <div
                  class="
                    tw-text-sf-neutral-dark
                    tw-font-medium
                    tw-text-lg
                    tw-mb-3
                    tw-pb-2
                  "
                >
                  {{ I18n.t('detailed_entry.top5') }}
                </div>
                <table class="sf-table sf-table-border sf-full-width">
                  <thead>
                    <tr>
                      <th>
                        <span>{{ I18n.t('detailed_entry.relation') }}</span>
                      </th>
                      <th class="sf-alignr">
                        <span>{{ I18n.t('detailed_entry.value') }}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in top5" :key="index">
                      <td>{{ item.relation }}</td>
                      <td class="sf-alignr">
                        {{ item.value }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Account Messages -->
              <div>
                <div class="tw-flex tw-items-center tw-justify-between tw-p-4">
                  <div
                    class="tw-text-sf-neutral-dark tw-font-medium tw-text-lg"
                  >
                    {{
                      presentationMode
                        ? I18n.t('detailed_entry.sidebar.meeting_messages')
                        : I18n.t('detailed_entry.sidebar.account_messages')
                    }}
                  </div>
                  <SfButtonDs
                    v-if="canAddMessage"
                    variant="primary"
                    class="tw-text-center"
                    data-walkthrough-id="new-remark-button"
                    @click="openRemarkModal"
                  >
                    {{ I18n.t('communication.add_remark') }}
                  </SfButtonDs>
                </div>
                <IsolatedSidepaneCommunication
                  v-if="accountId && ledgerId"
                  context-type="account"
                  :context-id="accountId"
                  :ledger-id="ledgerId"
                  :show-empty-state="true"
                />
              </div>
            </template>
          </div>
        </div>
      </template>
    </template>
  </SfModalPortal>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, provide } from 'vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { SfModalPortal } from '@app/shared/vue/components/sf-modal'
import { modalSizes } from '@app/shared/vue/components/sf-modal/constants'

import SfTable from '@app/shared/vue/components/sf-table/index.vue'
import EntriesTable from '@app/application/report/components/entries-table.vue'
import AttachmentListItem from '@app/application/sidebar-file-attachments/attachment-list-item.vue'
import IsolatedSidepaneCommunication from '@app/application/report/components/isolated-sidepane-communication.vue'
import RemarkSkeleton from '@app/shared/vue/components/molecules/remark/remark-skeleton.vue'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'

import { SfLog } from '@app/adapters/logger'
import SFetch from '@app/utils/sfetch'
import useAccountData from '@app/application/report/composables/use-account-data'
import { getDocumentPreview } from '@app/shared/document-preview/api'
import { documentPreviewHandlerKey } from '@app/application/report/injection-keys'
import talkingPointRemarkPayload from '@app/application/report/utils/talking-point-remark-payload'

import type { ModalPortalPayloadMap } from '@app/types/index'
import type { Attachment } from '@app/application/sidebar-file-attachments/types'
import type { DocumentPreviewResponse } from '@app/shared/document-preview/types'
import { DocumentLinkType } from '@app/shared/types/sf-data'

type AccountDetailsPayload =
  ModalPortalPayloadMap['report-account-details-modal']

type AdjustmentCategory = 'external' | 'internal'

type TogglePostedFields =
  | { canTogglePosted: true; togglePostedUrl: string }
  | { canTogglePosted: false; togglePostedUrl?: undefined }

type AdjustmentRow = {
  id?: string | number | null
  adjustmentId: number
  adjustmentUlid: number
  accountEntryId: number
  category: AdjustmentCategory
  description: string
  value: string
  posted: boolean
  canShow: boolean
  canEdit: boolean
  [key: string]: unknown
} & TogglePostedFields

const I18n = useI18n()

const CLICKABLE_ADJUSTMENT_CLASS =
  'tw-bg-transparent tw-border-0 tw-p-0 tw-text-sf-primary-dark hover:tw-underline tw-cursor-pointer'

const presentationMode = window.Silverfin.presentationMode

const loading = ref(false)
const errored = ref(false)
const accountId = ref(0)
const reportValue = ref('')
const documentPreview =
  ref<{
    attachment: Attachment
    response: DocumentPreviewResponse
  } | null>(null)

async function handleDocumentPreview(
  documentId: number,
  documentProperties?: Record<string, unknown>
) {
  const response = await getDocumentPreview(documentId)
  if (response) {
    documentPreview.value = {
      attachment: {
        document_id: documentId,
        name: String(documentProperties?.name || ''),
        size: Number(documentProperties?.size || 0),
        previewable: true,
        linkType: DocumentLinkType.Document,
      },
      response,
    }
  }
}

function closeDocumentPreview() {
  documentPreview.value = null
}

provide(documentPreviewHandlerKey, handleDocumentPreview)

const {
  ledgerLabel,
  ledgerId,
  accountTitle,
  accountSubtitle,
  accountLedgerUrl,
  top5,
  canAddMessage,
  detailedEntries,
  adjustments,
  fetchAccountData,
  refetch,
} = useAccountData()

function isValueHeader(headerKey: string): boolean {
  return headerKey === 'value'
}

// AdjustmentsData.entries is typed as Record<string, unknown>[] for the
// detailed-entries side of the API. The backend hash for adjustments
// returns the AdjustmentRow shape; the cast is the boundary between the
// two contracts.
function toAdjustmentRows(entries: Record<string, unknown>[]): AdjustmentRow[] {
  return entries as unknown as AdjustmentRow[]
}

const adjustmentRows = computed<AdjustmentRow[]>(() => {
  return [...toAdjustmentRows(adjustments.value.entries)].sort((a, b) => {
    if (a.posted === b.posted) return 0
    return a.posted ? 1 : -1
  })
})

function isAdjustmentClickable(entry: AdjustmentRow): boolean {
  return entry.canEdit || entry.canShow
}

function openAdjustment(entry: AdjustmentRow): void {
  const mode = entry.canEdit ? 'edit' : 'show'
  // openModalPortal closes the slide-over; ModalCitizen keeps the command
  // in its history. The adjustments-modal-closed listener below restores
  // the slide-over via openPreviousModalOf when the user dismisses the modal.
  window.modalCitizen.openModalPortal('adjustments-modal', {
    mode,
    adjustmentId: String(entry.adjustmentId),
    accountEntryId: entry.accountEntryId,
    ledgerId: ledgerId.value,
  })
}

// Keyed by togglePostedUrl (unique per adjustment) so the button is disabled
// while its request is in flight, preventing double-submits.
const togglingUrls = ref(new Set<string>())

async function togglePosted(entry: AdjustmentRow): Promise<void> {
  if (!entry.canTogglePosted) return
  if (togglingUrls.value.has(entry.togglePostedUrl)) return

  togglingUrls.value.add(entry.togglePostedUrl)
  try {
    await SFetch.jsonWithAlert(entry.togglePostedUrl, {
      method: 'POST',
      headers: { Accept: 'application/json' },
    })
    await refetch()
  } finally {
    togglingUrls.value.delete(entry.togglePostedUrl)
  }
}

function onAdjustmentsModalClosed(): void {
  // Restore the slide-over with its original payload; its onOpened handler
  // re-fetches account_details, picking up any edits made in the modal.
  window.modalCitizen.openPreviousModalOf('report-account-details-modal')
}

onMounted(() => {
  window.eventHub.$on('adjustments-modal-closed', onAdjustmentsModalClosed)
})

onUnmounted(() => {
  window.eventHub.$off('adjustments-modal-closed', onAdjustmentsModalClosed)
})

// When close-on-escape is false, sf-modal emits closeAttempt instead of
// closing automatically. We use this to only close the account-details
// modal when no child modal (XHR or Vue) is displayed on top.
function onCloseAttempt() {
  if (!window.modalCitizen.hasOpenOverlayModal()) {
    window.modalCitizen.closeModalPortal()
  }
}

async function onOpened(payload: AccountDetailsPayload) {
  loading.value = true
  errored.value = false
  documentPreview.value = null
  accountId.value = payload.accountId
  reportValue.value = payload.value || ''

  try {
    await fetchAccountData(
      payload.reportId,
      payload.accountId,
      payload.ledgerId,
      payload.subtractLedgerId || 0,
      payload.relative || 'YTD',
      payload.analyticalDimensionId
    )
  } catch (error) {
    // A page left open while the company narrows its visible periods keeps its
    // live links, and ReportsController#account_details answers a period the
    // user may not see with a 403. Without this the skeletons never come down.
    errored.value = true
    SfLog.error('Failed to load account details', {
      error,
      extra: {
        accountId: payload.accountId,
        reportId: payload.reportId,
        ledgerId: payload.ledgerId,
      },
    })
  } finally {
    loading.value = false
  }
}

function onClose() {
  loading.value = false
  errored.value = false
  documentPreview.value = null
}

function openRemarkModal() {
  window.modalCitizen.openModal('new-remark', {
    ownerType: 'Account',
    ownerId: accountId.value,
    ledgerId: ledgerId.value,
    ledgerLabel: ledgerLabel.value,
    hidePeriodSelector: true,
    allRemarkTags: window.allRemarkTags || {},
    showTags: true,
    ...talkingPointRemarkPayload(presentationMode),
  })
}
</script>

<style scoped>
/* Override the default sticky offset (55px for app header) since the table
   is inside a modal where the scroll container starts at the top. */
:deep(.sf-table-sticky th) {
  --sticky-offset: 0;
}

/* border-collapse: collapse causes borders to not move with sticky elements.
   Using separate with zero spacing gives the same look without the gap. */
:deep(.sf-table-sticky) {
  border-collapse: separate;
  border-spacing: 0;
}

/* Make the SfTable wrapper the scroll container for both axes so that sticky
   headers and horizontal scrolling both work. The title and search bar stay
   above the scroll area. */
:deep(.entries-table-container) {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

:deep(.entries-table-container > div:first-child) {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

:deep(.entries-table-scroll-container) {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

/* Make AttachmentListItem fill the available space inside the preview wrapper */
.document-preview-wrapper :deep(.attachment-list__item) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.document-preview-wrapper :deep(.attachment-preview-container) {
  flex: 1;
  height: auto !important;
  min-height: 0;
}
</style>
